import type { TileDisplay, TileResult, WidgetType } from "@csbi/contracts"
import type { MetricDefinition } from "@csbi/metric-catalog"
import type { Downgrade, ThemeMode } from "@csbi/viz-core"
import { isChartWidget } from "@csbi/viz-core"
import { ChartWidget, useVizModel } from "./chart-widget.js"
import { StatWidget } from "./stat-widget.js"
import { TableView, toCsv } from "./table-view.js"
import { WidgetFrame } from "./widget-frame.js"

export type TileWidgetProps = {
  title: string
  widget: WidgetType
  result?: TileResult
  display?: Partial<TileDisplay>
  definitions: ReadonlyMap<string, MetricDefinition>
  mode: ThemeMode
  height?: number | string
  loading?: boolean
  dimmed?: boolean
  error?: string
  hero?: boolean
  actions?: boolean
  onRefresh?: () => void
  refreshing?: boolean
  onSeriesToggle?: (name: string, visible: boolean) => void
  onDrill?: (payload: { seriesName: string; category: string; value: number | null }) => void
  categoryLabel?: (raw: string) => string
}

export function TileWidget(props: TileWidgetProps) {
  const { title, result, height, loading, error, onRefresh, actions = true } = props

  // useVizModel cannot be called when there is no result (hooks cannot be conditional), hence two components
  if (!result) {
    return (
      <WidgetFrame
        title={title}
        {...(loading ? { loading } : {})}
        {...(error ? { error } : {})}
        {...(actions && onRefresh ? { onRefresh } : {})}
        height={height ?? "100%"}
      >
        {null}
      </WidgetFrame>
    )
  }
  return <ResolvedTile {...props} result={result} />
}

function ResolvedTile({
  title,
  widget: requested,
  result,
  display,
  definitions,
  mode,
  height,
  dimmed,
  error,
  hero,
  actions = true,
  onRefresh,
  refreshing,
  onSeriesToggle,
  onDrill,
  categoryLabel,
}: TileWidgetProps & { result: TileResult }) {
  const model = useVizModel(result, requested, definitions, mode, categoryLabel)
  const downgrade: Downgrade | undefined = model.downgrade

  /**
   * ⚠ **The requested widget is not always the one drawn.** `downgrade.ts` turns a two-slice pie into a stat
   * tile, and a stat tile has no marks — so a drill handler attached from the *spec* put a "you can click
   * this" mark on a card with nothing to click. The affordance has to follow the rendered shape, which is
   * only known here, after the model has decided.
   */
  const drawsMarks = isChartWidget(model.widget) && result.state === "ok"

  const table = <TableView result={result} mode={mode} {...(categoryLabel ? { categoryLabel } : {})} />
  const body =
    model.widget === "table" ? (
      table
    ) : model.widget === "stat-tile" ? (
      <StatWidget result={result} definitions={definitions} mode={mode} {...(hero ? { hero } : {})} />
    ) : isChartWidget(model.widget) ? (
      <ChartWidget
        result={result}
        widget={requested}
        {...(display ? { display } : {})}
        definitions={definitions}
        mode={mode}
        title={title}
        {...(dimmed ? { dimmed } : {})}
        {...(onSeriesToggle ? { onSeriesToggle } : {})}
        {...(onDrill ? { onDrill } : {})}
        {...(categoryLabel ? { categoryLabel } : {})}
      />
    ) : (
      table
    )

  return (
    <WidgetFrame
      title={title}
      result={result}
      {...(downgrade ? { downgrade } : {})}
      {...(error ? { error } : {})}
      {...(actions && onRefresh ? { onRefresh } : {})}
      {...(actions && refreshing ? { refreshing } : {})}
      height={height ?? "100%"}
      {...(actions ? { onExportCsv: () => downloadCsv(`${title}.csv`, toCsv(result)) } : {})}
      {...(onDrill && drawsMarks ? { drillable: true } : {})}
      {...(actions ? { tableView: table } : {})}
    >
      {body}
    </WidgetFrame>
  )
}

/** Exported so the section export hands the browser a file the same way \u2014 the BOM below is easy to forget once */
export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
